import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  MessageCircle, 
  Send, 
  X, 
  Paperclip,
  Check,
  CheckCheck,
  User,
  Minimize2,
  Maximize2,
  Image,
  FileText,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatWindow = ({ oficialId, oficialName, onClose, isMinimized, onToggleMinimize }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (oficialId) {
      fetchChat();
    }
  }, [oficialId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (!conversationId) return;
    
    const interval = setInterval(() => {
      fetchNewMessages();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [conversationId]);

  const fetchChat = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/chat/with/${oficialId}`);
      setMessages(res.data.messages || []);
      setConversationId(res.data.conversation_id);
      
      // Mark messages as read
      if (res.data.conversation_id && res.data.unread_count > 0) {
        await axios.post(`${API}/chat/conversation/${res.data.conversation_id}/read-all`);
      }
    } catch (error) {
      console.error('Error fetching chat:', error);
      toast.error('Error al cargar el chat');
    } finally {
      setLoading(false);
    }
  };

  const fetchNewMessages = async () => {
    if (!conversationId) return;
    try {
      const res = await axios.get(`${API}/chat/messages/${conversationId}?limit=50`);
      if (res.data.length > messages.length) {
        setMessages(res.data);
        // Mark new messages as read
        await axios.post(`${API}/chat/conversation/${conversationId}/read-all`);
      }
    } catch (error) {
      // Silently fail for background polling
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes (JPG, PNG, WEBP) y PDFs');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 10MB');
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachmentPreview({
          file,
          type: 'image',
          preview: e.target.result,
          name: file.name
        });
      };
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview({
        file,
        type: 'pdf',
        preview: null,
        name: file.name
      });
    }
  };

  const handleSendWithAttachment = async () => {
    if (!attachmentPreview) return;

    try {
      setUploading(true);
      
      // Upload file
      const formData = new FormData();
      formData.append('file', attachmentPreview.file);
      formData.append('receiver_id', oficialId);
      
      const uploadRes = await axios.post(`${API}/chat/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (uploadRes.data.success) {
        // Send message with attachment
        await axios.post(`${API}/chat/messages`, {
          receiver_id: oficialId,
          content: newMessage.trim() || `📎 ${attachmentPreview.name}`,
          attachment_url: uploadRes.data.attachment_url,
          attachment_type: uploadRes.data.attachment_type
        });
        
        setNewMessage('');
        setAttachmentPreview(null);
        await fetchNewMessages();
        toast.success('Archivo enviado');
      }
    } catch (error) {
      toast.error('Error al enviar archivo');
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    // If there's an attachment, send with attachment
    if (attachmentPreview) {
      await handleSendWithAttachment();
      return;
    }
    
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setSending(true);
    
    // Optimistic update - show message immediately
    const tempId = `temp-${Date.now()}`;
    const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;
    const tempMessage = {
      id: tempId,
      content: messageContent,
      sender_id: currentUserId,
      created_at: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');

    // Retry logic - try up to 3 times
    let attempts = 0;
    const maxAttempts = 3;
    
    while (attempts < maxAttempts) {
      try {
        await axios.post(`${API}/chat/messages`, {
          receiver_id: oficialId,
          content: messageContent
        }, {
          timeout: 10000 // 10 second timeout
        });
        
        // Success - fetch updated messages
        await fetchNewMessages();
        inputRef.current?.focus();
        break; // Success, exit retry loop
        
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          // Remove optimistic message on final failure
          setMessages(prev => prev.filter(m => m.id !== tempId));
          const errorMsg = error.response?.data?.detail || 
                          error.code === 'ECONNABORTED' ? "Tiempo de espera agotado" :
                          "Error al enviar mensaje";
          toast.error(`${errorMsg}. Por favor intenta de nuevo.`);
        } else {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * attempts));
        }
      }
    }
    
    setSending(false);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;

  if (isMinimized) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="fixed bottom-4 right-4 z-50"
      >
        <button
          onClick={onToggleMinimize}
          className="flex items-center gap-3 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg transition-colors"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">Chat con {oficialName?.split(' ')[0]}</span>
          <Maximize2 className="w-4 h-4" />
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-navy-100 flex flex-col overflow-hidden z-50"
      data-testid="chat-window"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{oficialName}</h3>
            <p className="text-emerald-100 text-xs">Oficial Migratorio</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleMinimize}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <Minimize2 className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-navy-50/50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="w-12 h-12 text-navy-200 mb-3" />
            <p className="text-navy-500 text-sm">No hay mensajes aún</p>
            <p className="text-navy-400 text-xs mt-1">Inicia la conversación con tu oficial</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <div key={date}>
              <div className="flex items-center justify-center mb-3">
                <span className="px-3 py-1 bg-navy-100 text-navy-500 text-xs rounded-full">
                  {date}
                </span>
              </div>
              <div className="space-y-2">
                {dateMessages.map((msg) => {
                  const isOwn = msg.sender_id === currentUserId;
                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                          isOwn
                            ? 'bg-emerald-600 text-white rounded-br-md'
                            : 'bg-white text-navy-900 rounded-bl-md shadow-sm'
                        }`}
                      >
                        {/* Attachment */}
                        {msg.attachment_url && (
                          <div className="mb-2">
                            {msg.attachment_type === 'image' ? (
                              <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                                <img 
                                  src={msg.attachment_url} 
                                  alt="Adjunto" 
                                  className="max-w-full rounded-lg max-h-48 object-cover"
                                />
                              </a>
                            ) : (
                              <a 
                                href={msg.attachment_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 p-2 rounded-lg ${
                                  isOwn ? 'bg-emerald-700' : 'bg-navy-100'
                                }`}
                              >
                                <FileText className="w-5 h-5" />
                                <span className="text-sm underline">Ver PDF</span>
                              </a>
                            )}
                          </div>
                        )}
                        <p className="text-sm break-words">{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          isOwn ? 'text-emerald-100' : 'text-navy-400'
                        }`}>
                          <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                          {isOwn && (
                            msg.read 
                              ? <CheckCheck className="w-3 h-3" />
                              : <Check className="w-3 h-3" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Attachment Preview */}
      {attachmentPreview && (
        <div className="px-3 py-2 border-t border-navy-100 bg-navy-50">
          <div className="flex items-center gap-3">
            {attachmentPreview.type === 'image' ? (
              <img 
                src={attachmentPreview.preview} 
                alt="Preview" 
                className="w-16 h-16 object-cover rounded-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center">
                <FileText className="w-8 h-8 text-red-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy-900 truncate">{attachmentPreview.name}</p>
              <p className="text-xs text-navy-500">{attachmentPreview.type === 'image' ? 'Imagen' : 'PDF'}</p>
            </div>
            <button
              onClick={() => setAttachmentPreview(null)}
              className="p-1 hover:bg-navy-200 rounded-full"
            >
              <X className="w-4 h-4 text-navy-500" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-navy-100 bg-white">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-2 hover:bg-navy-100 rounded-full transition-colors"
          >
            <Paperclip className="w-5 h-5 text-navy-500" />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={attachmentPreview ? "Añade un mensaje (opcional)..." : "Escribe un mensaje..."}
            className="flex-1 px-4 py-2 bg-navy-50 border border-navy-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            disabled={sending || uploading}
            data-testid="chat-input"
          />
          <Button
            type="submit"
            disabled={(!newMessage.trim() && !attachmentPreview) || sending || uploading}
            className="w-10 h-10 p-0 bg-emerald-600 hover:bg-emerald-700 rounded-full"
            data-testid="chat-send-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default ChatWindow;
